"use client";

import useDates from "@/hooks/useDates";
import {
  Box,
  Button,
  Card,
  CardContent,
  NumberField,
  RenderIf,
  Typography,
} from "@repo/ui";

import { Building, Monitor, User } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { formatCurrency } from "@/lib/helpers";
import NoScheduleCartDownloadSyllabus from "../common/NoScheduleCartDownloadSyllabus";
import { useCountryCode } from "@/hooks/useCountryCode";
import RegistrationCloseTimer from "../common/RegistrationCloseTimer";
import clsx from "clsx";
import { useAddCartItemMutation } from "@/store/features/cartApiSlice";
import { getCookie, hasCookie } from "cookies-next";
import FastFillingLabel from "../common/FastFillingLabel";

type FastFillingScheduleProps = {
  data?: ServiceEvent;
};

const FastFillingSchedule = ({ data }: FastFillingScheduleProps) => {

  const [quantity, setQuantity] = useState<number>(1);
  const [addToCart, { isLoading }] = useAddCartItemMutation();
  const { country } = useCountryCode();
  const router = useRouter();

  const handleAddToCart = () => {
    const guestUserId = hasCookie("guestId")
      ? String(getCookie("guestId"))
      : undefined;
    addToCart({
      countryCode: country,
      guestUserId: guestUserId,
      serviceId: data!.serviceId,
      eventId: data!.id,
      offerId: data!.offerId,
      eventDetailId: data!.eventDetail.id,
      quantity: quantity || 1,
    }).then(() => {
      router.push(`/${country}/order-summary`);
    });
  };

  const { formattedDate, weekLabel, numberOfDays } = useDates(
    data?.eventDates || []
  );



  const days = numberOfDays > 1 ? "Days" : "Day";

  if (!data) {
    return <NoScheduleCartDownloadSyllabus />;
  }

  return (
    <span className="relative">
      <RegistrationCloseTimer closeDate={data?.eventCloseDate} />

      <Card
        outlined
        className={clsx("", {
          "!border-2 !border-secondary-main": data?.eventCloseDate,
          "!border-primary-main": !data?.eventCloseDate,
        })}
      >
        <CardContent>
          <Box className="flex gap-2 my-2">
            <span className="inline-block px-3 py-1 bg-primary-main/15 text-primary-main rounded-full text-xs font-medium transition-transform hover:scale-105">
              {weekLabel}
            </span>

            {data.isEarlyBirdOfferApplied && (
              <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
                Early bird
              </span>
            )}
          </Box>
          {data?.hasMappedService && (
            <Typography
              variant="h5"
              render={<span />}
              className="block mb-2 font-semibold"
            >
              {data?.eventName}
            </Typography>
          )}
          <h3 className="font-bold my-2 text-gray-900">
            {formattedDate} {`(${numberOfDays} ${days})`}
          </h3>
          <Typography variant="body2" className="!text-gray-900 mb-2">
            {data?.eventDetail?.startTime} - {data?.eventDetail?.endTime}{" "}
            {data?.eventDetail?.timezone}
            <span>({data?.eventDetail?.timeDiffInHours} Hrs / Day)</span>
          </Typography>
          <Box className="flex items-center space-x-2 mb-2">
            {data?.modeOfTraining === "online" ? (
              <Monitor className="text-primary-main" size={20} />
            ) : (
              <Building className="text-primary-main" size={20} />
            )}

            <Typography variant="body2">{data?.modeOfTraining}</Typography>
          </Box>
          <Box className="flex items-center space-x-2 mb-2">
            <b>Exam : </b>
            <Typography variant="body2">
              {data.withExam ? "Included" : "Not Included"}
            </Typography>
          </Box>
          {data?.eventDetail?.trainer && (
            <Box className="flex items-center space-x-2 mb-2">
              <User className="text-primary-main" size={20} />
              <Typography variant="body2">
                By {data?.eventDetail?.trainer?.name}
              </Typography>
            </Box>
          )}

          <Box className="mt-3 flex items-center gap-2 justify-between">
            <Box className="flex flex-col flex-1">
              <Typography
                variant="h5"
                render={<span />}
                className="font-bold !text-lg"
              >
                {formatCurrency(
                  data.eventDetail.price * quantity,
                  data.eventDetail.currency
                )}
              </Typography>
              <RenderIf condition={data.eventDetail.discountValue > 0}>
                <Typography
                  variant="h5"
                  render={<span />}
                  className="font-bold !text-sm line-through text-gray-500"
                >
                  {formatCurrency(
                    (data.eventDetail.price + data.eventDetail.discountValue) *
                      quantity,
                    data.eventDetail.currency
                  )}
                </Typography>
              </RenderIf>
            </Box>
            <Box>
              <NumberField
                label="Number of learners"
                noLabel
                min={1}
                onValueChange={(value) => setQuantity(value || 1)}
                inputProps={{
                  readOnly: true,
                  className: "!w-14",
                }}
              />
            </Box>
          </Box>
          <RenderIf condition={data.isEarlyBirdOfferApplied}>
            <Box className="mt-2">
              {data.eventDetail.discountValue && (
                <span className="text-sm font-medium text-green-600">
                  {data.eventDetail.discountPercent} Discount + 4 % Processing
                  Fees Waiver
                </span>
              )}
            </Box>
          </RenderIf>

          <Button
            color="primary"
            fullWidth
            loading={isLoading}
            onClick={handleAddToCart}
            className="mt-3"
          >
            Enroll Now
          </Button>
          <RenderIf condition={data.isFastFilling}>
            <FastFillingLabel className="mt-3" />
          </RenderIf>
        </CardContent>
      </Card>
    </span>
  );
};

export default FastFillingSchedule;
